import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, GitBranch, FilePdf, Buildings, ChartLineUp,
  UsersFour, ClipboardText, UploadSimple, EyeSlash, PaintBrush,
  ArrowRight, CheckCircle, Lightning,
} from '@phosphor-icons/react';
import MarketingNav from '../../components/marketing/MarketingNav';
import MarketingFooter from '../../components/marketing/MarketingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Vooxy landing page. Structure:
 *   1. Hero — headline, subhead, dual CTA, trust bar
 *   2. Feature grid (10 pillars pulled from PRD)
 *   3. "Why teams switch" split section
 *   4. Workflow visualization (5-level approval hierarchy)
 *   5. Social proof
 *   6. Final CTA
 *
 * Design: deep navy background (#050B18), emerald accents (#10B981),
 * IBM Plex + Outfit typography. Left-aligned hero, asymmetric feature grid,
 * generous whitespace. NO purple gradients, NO centered marketing-template
 * cliches. Scroll-linked fade-in via IntersectionObserver.
 */
export default function Landing() {
  useEffect(() => {
    document.title = 'Vooxy — CRM & Quotation OS for B2B teams';
    // Scroll fade-in
    const els = document.querySelectorAll('[data-fade]');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('opacity-100', 'translate-y-0');
          e.target.classList.remove('opacity-0', 'translate-y-4');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#050B18] text-white antialiased" data-testid="marketing-landing">
      <MarketingNav />

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Ambient background — subtle radial glow + dot grid, NOT a gradient */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-emerald-500/[0.08] blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-medium mb-8" data-testid="hero-badge">
              <Lightning size={14} weight="fill" />
              Feb 2026 — Multi-tenant SaaS is live
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-headline"
            >
              Quote-to-cash,{' '}
              <span className="text-emerald-400">without the chaos.</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl mb-10" data-testid="hero-subhead">
              Vooxy is the CRM & quotation OS built for teams with real approval hierarchy. Ship quotes faster, catch lost deals sooner, and give every stakeholder — from sales exec to President — exactly the view they need.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-16">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-emerald-500 text-[#050B18] font-semibold hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:-translate-y-0.5"
              >
                Talk to sales
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/10 text-slate-200 font-medium hover:border-white/20 hover:bg-white/[0.02] transition-colors"
              >
                See pricing
              </Link>
            </div>

            {/* Trust bar */}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span>Multi-tenant isolation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span>5-level approval hierarchy</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span>Audit-log everything</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span>White-labeled per workspace</span>
              </div>
            </div>
          </div>

          {/* Product screenshot mock — abstract dashboard preview */}
          <div className="mt-20 relative" data-fade>
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#0A1628] to-[#050B18] p-1 shadow-2xl shadow-emerald-500/10 opacity-0 translate-y-4 transition-all duration-1000">
              <div className="rounded-xl bg-[#0A1628] overflow-hidden">
                {/* Fake browser chrome */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                  </div>
                  <div className="ml-4 flex-1 max-w-md mx-auto px-3 py-1 rounded-md bg-black/30 text-xs text-slate-500 font-mono text-center">
                    acme.vooxy.app/dashboard
                  </div>
                </div>
                {/* Mock dashboard */}
                <div className="p-6 grid grid-cols-4 gap-4">
                  {[
                    { label: 'Active leads', value: '1,284', delta: '+12%' },
                    { label: 'Quotes this month', value: '347', delta: '+28%' },
                    { label: 'Won this quarter', value: '₹4.2Cr', delta: '+41%' },
                    { label: 'Gross margin', value: '38.4%', delta: '+2.1pt' },
                  ].map((k, i) => (
                    <div key={i} className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="text-xs text-slate-500 mb-1">{k.label}</div>
                      <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{k.value}</div>
                      <div className="text-xs text-emerald-400 mt-1">{k.delta}</div>
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-6 grid grid-cols-3 gap-4">
                  <div className="col-span-2 h-40 rounded-lg bg-gradient-to-br from-emerald-500/10 to-transparent border border-white/5 relative overflow-hidden">
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 160" preserveAspectRatio="none">
                      <polyline
                        points="0,120 40,110 80,95 120,100 160,80 200,70 240,55 280,60 320,40 360,35 400,20"
                        fill="none" stroke="#10B981" strokeWidth="2"
                      />
                      <polyline
                        points="0,120 40,110 80,95 120,100 160,80 200,70 240,55 280,60 320,40 360,35 400,20 400,160 0,160"
                        fill="url(#g1)" opacity="0.3"
                      />
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" />
                          <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="h-40 rounded-lg bg-white/[0.02] border border-white/5 p-4">
                    <div className="text-xs text-slate-500 mb-3">Pending approvals</div>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-2 mb-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        <div className="flex-1 h-2 rounded bg-white/5" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE GRID ─────────────────────────────────────────────── */}
      <section id="features" className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16" data-fade>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">Everything included</div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-6 opacity-0 translate-y-4 transition-all duration-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A CRM that understands how your business <span className="text-emerald-400">actually operates</span>.
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              Not a generic pipeline tool. Vooxy ships every feature real B2B sales orgs demand — from GST-compliant quotes to lost-order forensics.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-fade
                data-testid={`feature-card-${i}`}
                className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 hover:bg-white/[0.04] transition-all duration-300 opacity-0 translate-y-4"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 grid place-items-center mb-5 group-hover:bg-emerald-500/20 transition-colors">
                  <f.icon size={22} weight="duotone" className="text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {f.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY TEAMS SWITCH ─────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 border-t border-white/5 bg-[#0A1628]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div data-fade className="opacity-0 translate-y-4 transition-all duration-700">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">Why teams switch</div>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-6 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Built for the way your org <em className="not-italic text-emerald-400">already</em> approves deals.
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                Most CRMs assume a flat sales team. Real B2B looks like: Sales Exec → RSM → NSM → VP → President. Vooxy models that reality — every quote, discount, and exemption routes through the right level automatically.
              </p>
              <ul className="space-y-4">
                {[
                  ['5-level approval chain', 'Sales Exec, Regional Manager, National Manager, VP, President — each with configurable thresholds.'],
                  ['Region-aware visibility', 'Managers see their region; VPs see everything; execs see their own.'],
                  ['Discount escalation', 'Line-item discounts auto-route to the smallest authority that can approve them.'],
                  ['LORD (Lost Order Review)', 'Structured post-mortem on every lost deal — the intel your competitors never capture.'],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-4">
                    <CheckCircle size={22} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-white mb-1">{t}</div>
                      <div className="text-sm text-slate-400">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — approval flow diagram */}
            <div data-fade className="opacity-0 translate-y-4 transition-all duration-700" style={{ transitionDelay: '150ms' }}>
              <div className="relative p-8 rounded-2xl bg-gradient-to-br from-[#0A1628] to-[#050B18] border border-white/10 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/[0.08] rounded-full blur-3xl" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">Approval flow · live</div>
                  {[
                    { role: 'Sales Executive', name: 'Rahul K.', action: 'Submitted quote', delta: '₹8,42,000', color: 'from-blue-500/20 to-blue-500/5', accent: 'text-blue-300' },
                    { role: 'Regional Sales Mgr', name: 'Priya M.', action: 'Approved 12% discount', delta: 'Approved', color: 'from-purple-500/20 to-purple-500/5', accent: 'text-purple-300' },
                    { role: 'National Sales Mgr', name: 'Vikram S.', action: 'Reviewing GST exemption', delta: 'Pending', color: 'from-amber-500/20 to-amber-500/5', accent: 'text-amber-300' },
                    { role: 'Vice President', name: 'Neha D.', action: 'Awaiting review', delta: '—', color: 'from-slate-500/10 to-slate-500/5', accent: 'text-slate-400' },
                    { role: 'President', name: 'Arjun P.', action: 'Awaiting review', delta: '—', color: 'from-emerald-500/20 to-emerald-500/5', accent: 'text-emerald-300' },
                  ].map((s, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                      <div className={`flex items-center gap-4 p-3 rounded-lg bg-gradient-to-r ${s.color} border border-white/5`}>
                        <div className="w-9 h-9 rounded-full bg-white/10 grid place-items-center text-sm font-bold text-white flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {s.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-semibold uppercase tracking-wider ${s.accent}`}>{s.role}</div>
                          <div className="text-sm text-slate-300 truncate">{s.name} · {s.action}</div>
                        </div>
                        <div className="text-xs font-mono text-slate-400">{s.delta}</div>
                      </div>
                      {i < 4 && (
                        <div className="ml-6 my-1 w-px h-3 bg-gradient-to-b from-white/20 to-transparent" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF / STATS ─────────────────────────────────────── */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-16" data-fade>
            {[
              ['92%', 'Faster quote turnaround'],
              ['5×', 'Fewer discount leaks'],
              ['100%', 'Approval-chain compliance'],
              ['0', 'Cross-tenant data leaks. Ever.'],
            ].map(([n, l]) => (
              <div key={l} className="opacity-0 translate-y-4 transition-all duration-700" data-fade>
                <div className="text-5xl lg:text-6xl font-black text-emerald-400 mb-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{n}</div>
                <div className="text-sm text-slate-400 leading-tight">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-[1.05]" style={{ fontFamily: 'Outfit, sans-serif' }} data-fade>
            Stop losing deals to <span className="text-emerald-400">spreadsheet chaos</span>.
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto" data-fade>
            Book a 30-minute walkthrough. We&apos;ll map your existing approval hierarchy to Vooxy and show you your first quote flow live.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-fade>
            <Link
              to="/contact"
              data-testid="footer-cta-primary"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-emerald-500 text-[#050B18] font-bold hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40"
            >
              Book a walkthrough
              <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="/pricing"
              data-testid="footer-cta-secondary"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-white/10 text-slate-200 font-semibold hover:border-white/20 hover:bg-white/[0.02] transition-colors"
            >
              View plans
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

// 10 features pulled straight from the PRD
const FEATURES = [
  { icon: ShieldCheck, title: 'Strict RBAC & Regions', desc: 'Role-based access from Executive to President, region-scoped visibility, tenant-isolated data. Zero cross-contamination.' },
  { icon: GitBranch,   title: 'Multi-level Approvals', desc: 'Quotations, discounts, GST exemptions, bank accounts — each with its own escalation ladder that routes to the right approver automatically.' },
  { icon: FilePdf,     title: 'PDF Quote Generation', desc: 'Branded quotes, WCC, CSC, memos, proforma invoices — with product images, GST breakup, T&Cs. Ready to email to the buyer.' },
  { icon: ClipboardText, title: 'LORD (Lost Order Review)', desc: 'Structured post-mortems on every lost deal. Capture competitor, reason, buyer feedback. Turn lost pipeline into product insight.' },
  { icon: EyeSlash,    title: 'Accountability Memos', desc: 'On-the-record notes tied to leads, quotes and reps. Reviewable, exportable, and impossible to lose in a Slack thread.' },
  { icon: Buildings,   title: 'True Multi-tenancy', desc: 'Every workspace lives on its own subdomain. Data, users, and branding fully isolated at the database layer — not just the UI.' },
  { icon: ChartLineUp, title: 'COGS & Gross Margin', desc: 'Real profitability analytics per SKU, per quote, per region. Managers see margins alongside revenue instead of after the fact.' },
  { icon: UploadSimple, title: 'Bulk Imports & Exports', desc: 'CSV/Excel bulk-load for leads, products and customers with de-dup, GST validation, and per-row error reports. Ship data from Zoho in an afternoon.' },
  { icon: ClipboardText, title: 'Audit Log Every Action', desc: 'Full immutable trail of who did what, when, from where. Filter by actor, action, or target. Compliance-ready out of the box.' },
  { icon: PaintBrush,  title: 'White-labeled per Workspace', desc: 'Company logo, name, tagline, colors, browser tab, favicon — every tenant looks like their own product. Sell it to your own customers.' },
  { icon: UsersFour,   title: 'Team & Goal Tracking', desc: 'Hierarchical team view, per-rep goals, achievement dashboards. Managers see their sub-tree; execs see themselves. No spreadsheet gymnastics.' },
  { icon: ShieldCheck, title: 'GST-compliant Quoting', desc: 'IGST/CGST/SGST auto-split by region, GSTIN validation, and a proper exemption approval workflow. Built for Indian tax reality.' },
];
